import { Box, Card, CardContent } from "@mui/material";
import { lazy, Suspense } from "react";
import { Outlet, Route } from "react-router-dom";
import LogoIcon from "../assets/LogoIcon.svg";
import { DOT_BOOKING_GATE_PATH } from "../features/dot-booking-gate/constants";
import { POST_PROGRAM_VERIFICATION_PATH } from "../features/fcra-authorization/constants";
import { KICKOFF_INTERSTITIAL_PATH } from "../features/kickoff-interstitial/constants";
import { PERMIT_EXAM_GATE_PATH } from "../features/permit-exam-gate/constants";
import { REFERRAL_ONBOARDING_PATH } from "../features/referrals/constants";
import {
  COURSE_MAP_PATH,
  SECTION_DONE_PATH,
  SECTION_EXAM_PATH,
} from "../features/section-exams/constants";
import Logo from "../logo.svg";
import AppSkeleton from "../pages/AppSkeleton";
import ForgotPasswordScreen from "../pages/ForgotPasswordScreen";
import LoginScreen from "../pages/LoginScreen/LoginScreen";
import NotFoundScreen from "../pages/NotFoundScreen";
import OnboardingWaitlistGate from "../pages/OnboardingScreen/OnboardingWaitlistGate";
import AuthenticationLayout from "../pages/SignupScreen/AuthenticationLayout";
import BasicMenuLayout from "../pages/SignupScreen/BasicMenuLayout";
import SessionBoundary from "../SessionBoundary";
import BackHomeButton from "../shared/components/Button/BackHomeButton";
import FlowErrorBoundary from "../shared/components/FlowErrorBoundary";
import { PaddedLayout } from "../shared/components/Layout";
import PaddedLayoutContainer from "../shared/components/Layout/PaddedLayoutContainer";
import Template from "../shared/components/Layout/Template";
import { JOB_OFFER_INCOME_AFFIDAVIT_ROUTE_PATTERN } from "../shared/utils/jobOfferIncomeAffidavitDeepLink";
import { UserType } from "../types/User";

const WeekZeroAssessmentPage = lazy(
  () => import("../pages/WeekZeroAssessmentPage/WeekZeroAssessmentPage")
);
const SignupScreen = lazy(() => import("../pages/SignupScreen"));
const PostProgramVerificationScreen = lazy(
  () =>
    import(
      "src/features/fcra-authorization/components/PostProgramVerificationScreen"
    )
);
const CalEmbedScreen = lazy(() => import("src/pages/CalEmbedScreen"));
const ResourceScreen = lazy(() => import("src/pages/ResourceScreen"));
const UnitPage = lazy(() => import("src/pages/UnitPage"));
const KickoffInterstitialScreen = lazy(
  () =>
    import(
      "src/features/kickoff-interstitial/components/KickoffInterstitialScreen"
    )
);
const CourseMapScreen = lazy(
  () => import("src/features/section-exams/components/CourseMapScreen")
);
const SectionExamScreen = lazy(
  () => import("src/features/section-exams/components/SectionExamScreen")
);
const SectionDoneScreen = lazy(
  () => import("src/features/section-exams/components/SectionDoneScreen")
);
const PressureGaugeExercise = lazy(
  () => import("../components/PressureGaugeExercise")
);
const AllFlashcardDecksScreen = lazy(
  () => import("../pages/AllFlashcardDecksScreen")
);
const UserBackgroundSurveyScreen = lazy(
  () => import("../pages/BackgroundSurveyScreen/ApplicationScreen")
);
const WaitlistScreen = lazy(
  () => import("../pages/OnboardingScreen/WaitlistScreen")
);
const CourseTab = lazy(() => import("../pages/DashboardPage/CourseTab"));
const DashboardPage = lazy(
  () => import("../pages/DashboardPage/DashboardScreen")
);
const ProfileScreen = lazy(
  () => import("../pages/DashboardPage/ProfileScreen")
);
const ReferralScreen = lazy(
  () => import("../features/referrals/components/ReferralScreen")
);
const ReferralOnboardingScreen = lazy(
  () => import("../features/referrals/components/ReferralOnboardingScreen")
);
const SkillsTrainingOnboardingWizardScreen = lazy(
  () =>
    import(
      "../pages/DashboardPage/RangeTab/SkillsTrainingOnboardingWizardScreen"
    )
);
const YardOrientationScreen = lazy(
  () =>
    import("../pages/DashboardPage/RangeTab/YardOrientationOnboardingScreen")
);
const FinancialAidReceiptPage = lazy(
  () => import("../pages/FinancialAidReceiptPage")
);
const FinancialAidRequestPage = lazy(
  () => import("../pages/FinancialAidRequestPage")
);
const PermitTestBookingScreen = lazy(
  () => import("../pages/PermitTestBookingScreen")
);
const DmvAppointmentScreen = lazy(
  () => import("../pages/DmvAppointmentScreen")
);
const PermitExamGateScreen = lazy(
  () => import("../features/permit-exam-gate/components/PermitExamGateScreen")
);
const DotBookingGateScreen = lazy(
  () => import("../features/dot-booking-gate/components/DotBookingGateScreen")
);
const FlashcardDeckPage = lazy(() => import("../pages/FlashcardDeckScreen"));
const MentalHealthScreeningScreen = lazy(
  () => import("../pages/MentalHealthScreeningScreen")
);
const AutomatedConditionalAcceptanceScreen = lazy(
  () => import("../pages/OnboardingScreen/AutomatedConditionalAcceptanceScreen")
);
const EligibilityScreen = lazy(
  () => import("../pages/OnboardingScreen/EligibilityScreen")
);
const InterviewScreen = lazy(
  () => import("../pages/OnboardingScreen/InterviewScreen")
);
const JobPreferencesScreen = lazy(
  () => import("../pages/OnboardingScreen/JobPreferencesScreen")
);
const EmploymentPlanScreen = lazy(
  () => import("../pages/EmploymentPlanScreen/EmploymentPlanScreen")
);
const JobSearchSuccessPlanScreen = lazy(
  () => import("../pages/JobSearchSuccessPlan/JobSearchSuccessPlanScreen")
);
const ComplianceWizardScreen = lazy(
  () => import("../features/compliance/ComplianceWizardScreen")
);
const SuccessPlanOnboardingScreen = lazy(
  () => import("../pages/OnboardingScreen/SuccessPlanOnboardingScreen")
);
const TheoryExamPermitPlanWizard = lazy(
  () =>
    import(
      "../pages/OnboardingScreen/TheoryExamPermitPlanWizard/TheoryExamPermitPlanWizard"
    )
);
const DocumentScreen = lazy(
  () => import("../pages/Participant/DocumentScreen")
);
const SuccessPlanScreen = lazy(
  () => import("../pages/Participant/SuccessPlanScreen")
);
const PartnerHomeScreen = lazy(
  () => import("../pages/PartnerScreen/PartnerHomeScreen")
);
const PartnerLoginScreen = lazy(
  () => import("../pages/PartnerScreen/PartnerLoginScreen")
);
const PartnerSignupScreen = lazy(
  () => import("../pages/PartnerScreen/PartnerSignupScreen")
);
const PodcastScreen = lazy(() => import("../pages/PodcastScreen"));
const SkillsTrainingOrientationScreen = lazy(
  () =>
    import(
      "../pages/SkillsTrainingOrientationScreen/SkillsTrainingOrientationScreen"
    )
);
const WraparoundServiceScreen = lazy(
  () => import("../pages/WraparoundServiceScreen/WraparoundServiceScreen")
);
const ResumeBuilderScreen = lazy(
  () => import("../pages/ResumeBuilderScreen/ResumeBuilderScreen")
);
const JobMatchCallScreen = lazy(
  () => import("../pages/JobMatchCallScreen/JobMatchCallScreen")
);
const QuizScreenV2 = lazy(() => import("src/pages/QuizScreenV2/QuizScreenV2"));
// Dev-only: the Permit Flow v2 celebration preview harness. The component
// self-guards (returns null outside development), so its `/dev/celebrations`
// route renders nothing in production; the lazy chunk only loads if the path
// is visited.
const DevelopmentOnlyCelebrationsScreen = lazy(
  () => import("src/pages/QuizScreenV2/DevelopmentOnlyCelebrationsScreen")
);
const PracticeHubPage = lazy(
  () => import("src/pages/PracticeHubPage/PracticeHubPage")
);
// Dev-only: the welcome-back card preview harness. Self-guards (returns null
// outside development), so its `/dev/welcome-back` route renders nothing in
// production; the lazy chunk only loads if the path is visited.
const DevelopmentOnlyWelcomeBackScreen = lazy(
  () =>
    import(
      "src/features/welcome-back-card/components/DevelopmentOnlyWelcomeBackScreen"
    )
);
const JobOfferIncomeAffidavitScreen = lazy(
  () => import("src/pages/JobOfferIncomeAffidavitScreen")
);
const QuizPage = lazy(() => import("src/pages/QuizPage"));
const OnlineTrainingScreen = lazy(
  () => import("src/pages/OnlineTrainingScreen")
);
const CoachChatScreen = lazy(
  () => import("src/pages/Participant/CoachChatScreen")
);
const VendorLocationsScreen = lazy(
  () => import("src/pages/Vendors/VendorLocationsView")
);
const PrivacyPolicyScreen = lazy(
  () => import("../pages/PrivacyPolicyScreen/PrivacyPolicyScreen")
);
const TermsOfServiceScreen = lazy(
  () => import("../pages/TermsOfServiceScreen/TermsOfServiceScreen")
);

export function studentRoutes(): JSX.Element {
  return (
    <>
      <Route
        path="/login"
        element={
          <AuthenticationLayout
            variant="focused"
            corner={
              <img
                src={LogoIcon}
                alt="Emerge Career"
                style={{ width: 48, height: 48 }}
              />
            }
          >
            <LoginScreen />
          </AuthenticationLayout>
        }
      />
      <Route path="/register" element={<SignupScreen />} />
      <Route
        path="/wellbeing"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Box>
                <MentalHealthScreeningScreen />
              </Box>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/resume-builder"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <ResumeBuilderScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AuthenticationLayout
            corner={
              <img src={Logo} alt="Emerge Career" style={{ width: 300 }} />
            }
          >
            <ForgotPasswordScreen />
          </AuthenticationLayout>
        }
      />

      <Route
        path="/partners"
        element={
          <Suspense fallback={<AppSkeleton />}>
            <Outlet />
          </Suspense>
        }
      >
        <Route path="dashboard" element={<PartnerHomeScreen />} />

        <Route path="register" element={<PartnerSignupScreen />} />

        <Route path="login" element={<PartnerLoginScreen />} />
      </Route>

      <Route
        path="/ambassadors/register"
        element={
          <PartnerSignupScreen
            signupType="ambassador"
            bonusIncentiveVersion="2026-01"
          />
        }
      />

      <Route
        path="/ambassadors/login"
        element={<PartnerLoginScreen loginType="ambassador" />}
      />

      <Route
        path="/week-zero-assessment"
        element={
          <SessionBoundary userType={UserType.User}>
            <WeekZeroAssessmentPage />
          </SessionBoundary>
        }
      />

      <Route
        path="/flashcards"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <Template>
              <AllFlashcardDecksScreen />
            </Template>
          </SessionBoundary>
        }
      />

      <Route
        path="/flashcards/:id"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <Template>
              <FlashcardDeckPage />
            </Template>
          </SessionBoundary>
        }
      />

      <Route
        path="/onboarding"
        element={
          <SessionBoundary
            userType={UserType.User}
            // Allow all users (including deactivated/rejected) to access onboarding
            onboardingRedirectRule="allowAllAccountsAccess"
          >
            <FlowErrorBoundary flowName="onboarding">
              <Outlet />
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      >
        <Route
          path="waitlist"
          element={
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <Box margin="auto" maxWidth={800}>
                  <WaitlistScreen />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          }
        />
        <Route
          index
          element={
            <OnboardingWaitlistGate>
              <BasicMenuLayout>
                <PaddedLayout>
                  <Box margin="auto" maxWidth={800}>
                    <Card>
                      <CardContent>
                        <UserBackgroundSurveyScreen />
                      </CardContent>
                    </Card>
                  </Box>
                </PaddedLayout>
              </BasicMenuLayout>
            </OnboardingWaitlistGate>
          }
        />
        <Route
          path="survey"
          element={
            <OnboardingWaitlistGate>
              <BasicMenuLayout>
                <PaddedLayout>
                  <Box margin="auto" maxWidth={800}>
                    <Card>
                      <CardContent>
                        <UserBackgroundSurveyScreen />
                      </CardContent>
                    </Card>
                  </Box>
                </PaddedLayout>
              </BasicMenuLayout>
            </OnboardingWaitlistGate>
          }
        />
        <Route
          path="documents"
          element={
            <BasicMenuLayout>
              <BackHomeButton />
              <DocumentScreen />
            </BasicMenuLayout>
          }
        />
        <Route
          path="success-plan"
          element={
            <BasicMenuLayout navigationDisabled>
              {/* No PaddedLayout: SuccessPlanStepLayout owns its own padding
                  and the StoryViewer is full-bleed. PaddedLayout's 32px
                  mobile gutters made the cards look inset and squished. */}
              <Box
                sx={{
                  mx: "auto",
                  maxWidth: 800,
                  px: { xs: 0, sm: 2, lg: 4 },
                  py: { xs: 0, sm: 2 },
                }}
              >
                <SuccessPlanOnboardingScreen />
              </Box>
            </BasicMenuLayout>
          }
        />
        <Route
          path="finish"
          element={
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <Box margin="auto" maxWidth={800}>
                  <AutomatedConditionalAcceptanceScreen />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          }
        />
      </Route>
      <Route
        path="success-plan"
        element={
          <BasicMenuLayout>
            <PaddedLayout>
              <Box margin="auto" maxWidth={800}>
                <BackHomeButton />
                <SuccessPlanScreen />
              </Box>
            </PaddedLayout>
          </BasicMenuLayout>
        }
      />
      {/* PR-5 cutover: `/theory-exam/success-plan` renders the new
          Post-Course Permit-Attainment Wizard. The old
          `<TheoryExamOnboardingScreen>` was deleted in this same PR.
          The `/theory-exam/success-plan-v2` shadow route was removed —
          any external link that was using v2 (analytics, CIO emails)
          needs to drop the `-v2` suffix. */}
      <Route
        path="theory-exam/success-plan"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <TheoryExamPermitPlanWizard />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="skills-training/success-plan"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <Box margin="auto" maxWidth={600}>
                  <SkillsTrainingOnboardingWizardScreen />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      {/* Journey job-match call. Deliberately unlinked during the pilot: the
          only door is the auth-bound startJobMatchSession callable, so this
          route is reachable by URL but useless to anyone not signed in. */}
      <Route
        path="job-search/job-match-call"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <JobMatchCallScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="job-search/plan"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout>
              <Suspense fallback={<AppSkeleton />}>
                <EmploymentPlanScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="job-search/success-plan"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <JobSearchSuccessPlanScreen />
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      {/* Post-unit-1 Job Search Kickoff interstitial — the test arm of the
          `job-search-kickoff-after-unit-1` experiment. `useResourceNavigation`
          diverts here once on the advance out of the first unit; the `next`
          param carries the lesson to return to. Full-bleed brown poster, so
          no BasicMenuLayout. Not a shareable URL: landing here by hand just
          shows the offer and releases to the dashboard.
          See src/features/kickoff-interstitial/experiment.ts. */}
      <Route
        path={KICKOFF_INTERSTITIAL_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="kickoff-interstitial">
              <Suspense fallback={<AppSkeleton />}>
                <KickoffInterstitialScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* Section exams: the one-time course map on the advance out of lesson
          1.0, and the in-course exam runner reached at section boundaries and
          from the sectioned course list. `next` carries the exit. */}
      <Route
        path={COURSE_MAP_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="course-map">
              <Suspense fallback={<AppSkeleton />}>
                <CourseMapScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path={SECTION_DONE_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="section-done">
              <Suspense fallback={<AppSkeleton />}>
                <SectionDoneScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path={SECTION_EXAM_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="section-exam">
              <Template variant="v2" disableNavigation>
                <Suspense fallback={<AppSkeleton />}>
                  <SectionExamScreen />
                </Suspense>
              </Template>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="wizard/clp"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <ComplianceWizardScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      {/* Post-program verification (FCRA) for students already past
          enrollment: the home backfill card and coach links land here.
          Redirects home while the feature is off. */}
      <Route
        path={POST_PROGRAM_VERIFICATION_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <PostProgramVerificationScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="wizard/enrollment"
        element={
          <SessionBoundary userType={UserType.User}>
            <BasicMenuLayout navigationDisabled>
              <Suspense fallback={<AppSkeleton />}>
                <ComplianceWizardScreen />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="skill-training/orientation"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout navigationDisabled>
              <SkillsTrainingOrientationScreen />
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="skills-training/orientation"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout navigationDisabled>
              <SkillsTrainingOrientationScreen />
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="skills-training/introduction"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <PaddedLayoutContainer>
                  <YardOrientationScreen />
                </PaddedLayoutContainer>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="skills-training/practice/:id"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <PaddedLayout>
                <Box margin="auto" maxWidth={600}>
                  <PodcastScreen />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/skills-training/math/pressure_gauge_exercise"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <PaddedLayout>
                <Box margin="auto" maxWidth={600}>
                  <PressureGaugeExercise />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/courses/:vocationId"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <CourseTab />
          </SessionBoundary>
        }
      />
      <Route
        path="/units/:id"
        element={
          <SessionBoundary
            userType={UserType.User}
            // TODO: uncomment this
            // onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <UnitPage />
          </SessionBoundary>
        }
      />
      <Route
        path="/resources/:id"
        element={
          <SessionBoundary
            userType={UserType.User}
            // TODO: uncomment this
            // onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <FlowErrorBoundary flowName="resource">
              <Template>
                <ResourceScreen />
              </Template>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />

      <Route
        path="/quizzes"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <Suspense fallback={<AppSkeleton />}>
              <PracticeHubPage />
            </Suspense>
          </SessionBoundary>
        }
      />
      <Route
        path="/quizzes/:id"
        element={
          <SessionBoundary
            userType={UserType.User}
            // TODO: uncomment this
            // onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <FlowErrorBoundary flowName="quiz">
              <Suspense fallback={<AppSkeleton />}>
                <QuizPage />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="/online-training"
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="online-training">
              <Suspense fallback={<AppSkeleton />}>
                <OnlineTrainingScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="/practice"
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="practice">
              <Template variant="v2" disableNavigation>
                <Suspense fallback={<AppSkeleton />}>
                  <QuizScreenV2 />
                </Suspense>
              </Template>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="/practice-hub"
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="practice-hub">
              <Suspense fallback={<AppSkeleton />}>
                <PracticeHubPage />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* Standalone DMV permit-test auto-booking page — a directly shareable
          URL (see PERMIT_TEST_BOOKING_URL) so a student can be texted/emailed
          straight into booking. */}
      <Route
        path="/permit-test"
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="permit-test">
              <Suspense fallback={<AppSkeleton />}>
                <PermitTestBookingScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* The student's booked DMV appointment (date, office, barcode, DMV
          reschedule link) on its own URL (see DMV_APPOINTMENT_URL) so a coach
          can text/email a student who needs to reschedule straight to it. */}
      <Route
        path="/dmv-appointment"
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="dmv-appointment">
              <Suspense fallback={<AppSkeleton />}>
                <DmvAppointmentScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* Forced permit-exam booking gate. `useResourceNavigation` diverts
          here on the advance past the 10th completed lesson, and
          `useStudyActionPermitGate` diverts on opening the practice-exam
          screen; the `next` param carries the study path to return to. Not a
          shareable URL: landing here without the gate conditions releases
          straight through. */}
      <Route
        path={PERMIT_EXAM_GATE_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="permit-exam-gate">
              <Suspense fallback={<AppSkeleton />}>
                <PermitExamGateScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* Forced DOT-physical booking gate — the test arm of the
          `dot-booking-gate-lesson-7` double A/B. `useResourceNavigation`
          diverts here on the advance past the 6th completed video, and
          `useStudyActionDotGate` diverts on opening a lesson page in the same
          band; the `next` param carries the lesson to return to. Not a
          shareable URL: landing here without the gate conditions releases
          straight through. See src/features/dot-booking-gate/experiment.ts. */}
      <Route
        path={DOT_BOOKING_GATE_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="dot-booking-gate">
              <Suspense fallback={<AppSkeleton />}>
                <DotBookingGateScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      {/* Dev-only preview harness. The route is always registered, but
          DevelopmentOnlyCelebrationsScreen returns null outside development
          (the DevelopmentOnly* pattern), so in production this path renders
          nothing. Gating the <Route> itself on process.env.NODE_ENV trips
          eslint-config-react-app's no-unused-vars on the lazy import. */}
      <Route
        path="/dev/celebrations"
        element={
          <SessionBoundary userType={UserType.User}>
            <Suspense fallback={<AppSkeleton />}>
              <DevelopmentOnlyCelebrationsScreen />
            </Suspense>
          </SessionBoundary>
        }
      />
      {/* Dev-only preview harness for the welcome-back card's three variants.
          No SessionBoundary: the harness renders the card from mock content
          and needs no student, which is the point — the real trigger requires
          a CDL student with a populated plan and prior study activity.
          See src/features/welcome-back-card/experiment.ts. */}
      <Route
        path="/dev/welcome-back"
        element={
          <Suspense fallback={<AppSkeleton />}>
            <DevelopmentOnlyWelcomeBackScreen />
          </Suspense>
        }
      />
      <Route
        path="/coach"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="allowAllAccountsAccess"
          >
            <BasicMenuLayout>
              <PaddedLayout>
                <Box margin="auto" maxWidth={800}>
                  <Card>
                    <CardContent>
                      <Suspense fallback={<AppSkeleton />}>
                        <CoachChatScreen />
                      </Suspense>
                    </CardContent>
                  </Card>
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/interviews/:id"
        element={
          <SessionBoundary userType={UserType.User}>
            <InterviewScreen />
          </SessionBoundary>
        }
      />
      <Route
        path="/profile"
        element={
          <SessionBoundary
            userType={UserType.User}
            // TODO: uncomment this
            // onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <Template variant="v2">
              <ProfileScreen />
            </Template>
          </SessionBoundary>
        }
      />
      {/* One-time referral onboarding. `PostExamSequence` sends the student
          here from the exam report's "Return Home" after their third practice
          exam; no Template, it is a full-screen interstitial. No onboarding
          redirect rule, matching /practice-hub and the other post-exam gates:
          permit-first students take practice exams before they are
          "onboarded" and must land here, not on /onboarding. */}
      <Route
        path={REFERRAL_ONBOARDING_PATH}
        element={
          <SessionBoundary userType={UserType.User}>
            <FlowErrorBoundary flowName="referral-onboarding">
              <Suspense fallback={<AppSkeleton />}>
                <ReferralOnboardingScreen />
              </Suspense>
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="/refer"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <Template variant="v2">
              <ReferralScreen />
            </Template>
          </SessionBoundary>
        }
      />
      <Route
        path="/wraparound-service"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <WraparoundServiceScreen />
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/financial-aid/request"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <Suspense fallback={<AppSkeleton />}>
                <FinancialAidRequestPage />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/financial-aid/receipt/:financialAidId"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <Suspense fallback={<AppSkeleton />}>
                <FinancialAidReceiptPage />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path={JOB_OFFER_INCOME_AFFIDAVIT_ROUTE_PATTERN}
        // Public on purpose: students sign from a texted link without logging
        // in. The screen reads and writes only through the `*ByLink` callables.
        element={
          <BasicMenuLayout navigationDisabled>
            <Suspense fallback={<AppSkeleton />}>
              <JobOfferIncomeAffidavitScreen />
            </Suspense>
          </BasicMenuLayout>
        }
      />
      {/* TODO: make this route inaccessible unless the user is eligible to submit for a DOT physical */}
      <Route
        path="/locations/dot_physical"
        element={
          <SessionBoundary
            userType={UserType.User}
            onboardingRedirectRule="redirectToOnboardingIfRequired"
          >
            <BasicMenuLayout>
              <Suspense fallback={<AppSkeleton />}>
                <VendorLocationsScreen vendorType="dot_physical" />
              </Suspense>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="eligibility"
        element={
          <SessionBoundary>
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <Box margin="auto" maxWidth={800}>
                  <BackHomeButton />
                  <Card>
                    <CardContent>
                      <EligibilityScreen />
                    </CardContent>
                  </Card>
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="documents"
        element={
          <SessionBoundary>
            <BasicMenuLayout navigationDisabled>
              <PaddedLayout>
                <Box margin="auto" maxWidth={600}>
                  <BackHomeButton />
                  <DocumentScreen />
                </Box>
              </PaddedLayout>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        index
        path="/"
        element={
          <SessionBoundary onboardingRedirectRule="redirectToOnboardingIfRequired">
            <FlowErrorBoundary flowName="dashboard">
              <DashboardPage />
            </FlowErrorBoundary>
          </SessionBoundary>
        }
      />
      <Route
        path="/job-search/job-preferences"
        element={
          <SessionBoundary>
            <BasicMenuLayout navigationDisabled>
              {/* No PaddedLayout/Card — JobPreferencesStep renders
                  SuccessPlanStepLayout + StickyBottomCTA which expect to
                  own the viewport like the wizard does at /onboarding/success-plan. */}
              <Box
                sx={{
                  mx: "auto",
                  maxWidth: 800,
                  px: { xs: 0, sm: 2, lg: 4 },
                  py: { xs: 0, sm: 2 },
                }}
              >
                <JobPreferencesScreen />
              </Box>
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      <Route
        path="/appointments/schedule"
        element={
          <SessionBoundary onboardingRedirectRule="redirectToOnboardingIfRequired">
            <BasicMenuLayout>
              <CalEmbedScreen />
            </BasicMenuLayout>
          </SessionBoundary>
        }
      />
      {/* Public, unauthenticated — referenced by the Twilio A2P 10DLC campaign
          and the signup Terms/Privacy link. No SessionBoundary on purpose. */}
      <Route
        path="/privacy"
        element={
          <Suspense fallback={<AppSkeleton />}>
            <PrivacyPolicyScreen />
          </Suspense>
        }
      />
      <Route
        path="/terms"
        element={
          <Suspense fallback={<AppSkeleton />}>
            <TermsOfServiceScreen />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <Template>
            <NotFoundScreen />
          </Template>
        }
      />
    </>
  );
}
